import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import FilmFrontLogo from "@/components/FilmFrontLogo";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back to Film Front");
      const from = location.state?.from || "/app";
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid email or password";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white ff-grain relative overflow-hidden">
      <div className="ff-orange-orb -top-40 -left-40 opacity-70" />
      <div className="max-w-md mx-auto px-6 py-10 relative z-10">
        <Link to="/" className="inline-block mb-14" data-testid="login-back-home">
          <FilmFrontLogo size="md" />
        </Link>
        <div className="text-xs uppercase tracking-[0.3em] text-neutral-400 mb-3">Members sign in</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter leading-none font-medium mb-8">Back on set.</h1>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Email</span>
            <div className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[hsl(var(--ff-accent))] transition-colors">
              <Mail className="w-4 h-4 text-neutral-500" />
              <input
                type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@studio.com"
                className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
                data-testid="login-email-input"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Password</span>
            <div className="mt-1.5 flex items-center gap-2 bg-black border border-white/10 rounded-lg px-3 py-2.5 focus-within:border-[hsl(var(--ff-accent))] transition-colors">
              <Lock className="w-4 h-4 text-neutral-500" />
              <input
                type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-transparent flex-1 outline-none text-sm placeholder:text-neutral-600"
                data-testid="login-password-input"
              />
            </div>
          </label>
          {error && <div className="text-sm text-red-400" data-testid="login-error">{error}</div>}
          <button
            type="submit" disabled={loading}
            className="ff-btn-primary w-full mt-6 disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>}
          </button>
        </form>
        <div className="mt-8 text-sm text-neutral-400">
          New to Film Front?{" "}
          <Link to="/register" className="ff-accent-text hover:underline" data-testid="login-to-register-link">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
